import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { UserContext } from "../UserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Rating,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { getClient, putClient } from "../API/clientAPI";
import { getJobOrder, putJobOrder, patchJobOrderLink } from "../API/jobOrderAPI";
import { getTechnicians } from "../API/technicianAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import { sendEmailQueue } from "../API/emailAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AreasTable from "./FormComponents/AreasTable";
import FindingsCard from "./FormComponents/FindingsCard";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

// PDF uploaders & buckets
import {
  s3ReportsPDFUploader,
  REPORTS_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-reports";
import {
  s3InvoicePDFUploader,
  INVOICE_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-receipts";
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

// PDF generators
import generateReceipts from "./GenerateReceipts";
import generateReports from "./GenerateReports";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function EditQcForm() {
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');
  
  const [jobOrder, setJobOrder] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    chemical_used: [],
    job_date: "",
    // end_date: "",
    start_time: "",
    end_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});
  const [editedClient, setEditedClient] = useState();
  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [specialNotes, setSpecialNotes] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [treatmentType, setTreatmentType] = useState("");

  const [controlAgainst, setControlAgainst] = useState({
    crawlingInsects: false,
    flyingInsects:   false,
    rodentControl:   false,
    others:          false,
  });
  const [otherControlText, setOtherControlText] = useState("");

  // Signature-related state
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");
  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  const navigate = useNavigate();

  // RHF — single form instance for the page
  const {
    control,
    register,
    setValue,
    handleSubmit: handleFormSubmit,
  } = useForm({
    defaultValues: {
      findingsAreas: [
        {
          location: "",
          pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
        },
      ],
      recommendations: [
      ],
    },
  });

  // ---- FINDINGS AREAS ----
  const {
    fields: findingsAreas,
    append: addFinding,
    remove: removeFinding,
    replace: replaceFindingAreas,
  } = useFieldArray({
    control,
    name: "findingsAreas",
  });

  // ---- RECOMMENDATIONS ----
  const MAX_RECOMMENDATIONS = 8;
  
  const RECOMMENDATION_OPTIONS = [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
    // Added to support free-text:
    { value: "Other", label: "Other (specify)" },
  ];

  const {
    fields: recFields,
    append: appendRec,
    remove: removeRec,
  } = useFieldArray({
    control,
    name: "recommendations",
  });

  // Watch all recommendation rows to know when to show text fields
  const recValues = useWatch({ control, name: "recommendations" }) || [];

  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ];

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ];

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  // 4. for display purposes in non-editable sections
  const displayedJobTypes = selectedJobTypes;
  const displayedJobDescriptions = selectedJobDescriptions;

  const [chemical_used, setChemicalUsed] = useState([
    { label: "1", chemical: "" },
    { label: "2", chemical: "" },
    { label: "3", chemical: "" },
    { label: "4", chemical: "" },
    { label: "5", chemical: "" },
    { label: "6", chemical: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const handleControlAgainstChange = (field) => (event) => {
    setControlAgainst(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  // Type of Structure (checkboxes) + side text fields
  const [structureType, setStructureType] = useState({
    residential: false,
    commercialEstablishment: false,
    others: false,
  });

  const [structureNatureOfBusiness, setStructureNatureOfBusiness] = useState("");
  const [structureOthersText, setStructureOthersText] = useState("");

  const handleStructureTypeChange = (field) => (event) => {
    const checked = event.target.checked;
    setStructureType(prev => ({ ...prev, [field]: checked }));
  };

  // Nature of Work (checkboxes) + side text field
  const [natureOfWork, setNatureOfWork] = useState({
    generalPestControl: false,
    disinfection: false,
    fumigation: false,
    rodentControl: false,
    foggingMisting: false,
    others: false,
  });
  const [natureOfWorkOthersText, setNatureOfWorkOthersText] = useState("");

  const handleNatureOfWorkChange = (field) => (event) => {
    const checked = event.target.checked;
    setNatureOfWork(prev => ({ ...prev, [field]: checked }));
  };

  // For General Pest Control (GPC) section
  const [gpcFlyingCrawling, setGpcFlyingCrawling] = useState(false);
  const [gpcInsectsDetail, setGpcInsectsDetail] = useState({
    cockroach: false,
    flies: false,
    mosquitoes: false,
  });
  const [gpcRodents, setGpcRodents] = useState(false);
  const [gpcOtherPests, setGpcOtherPests] = useState(false);
  const [gpcOtherPestsText, setGpcOtherPestsText] = useState("");

  // Action Taken / Treatment Done section
  const [actionTaken, setActionTaken] = useState({
    mistSpraying: false,
    thermalFogging: false,
    coldFogging: false,
    larviciding: false,
    gelBaiting: false,
    uvLight: false,
    wiping: false,
    steaming: false,
    others: false,
  });
  const [actionTakenOthersText, setActionTakenOthersText] = useState("");

  const handleActionTakenChange = (field) => (event) => {
    const checked = event.target.checked;
    setActionTaken(prev => ({ ...prev, [field]: checked }));
  };

  // Customer's Responsibilities
  const [customerResp, setCustomerResp] = useState({
    sanitation1: "",
    sanitation2: "",
    sanitation3: "",
    sealing: "",
    barriers: "",
    repairsMaintenance: "",
    renovation: "",
  });

  // Findings - Degree of Infestation
  const [infestationFindings, setInfestationFindings] = useState({
    Roaches: "",
    Mosquitoes: "",
    Flies: "",
    Ants: "",
    Rodents: "",
  });

  const handleInfestationChange = (pest, level) => {
    setInfestationFindings(prev => ({ ...prev, [pest]: level }));
  };

  // --- AREAS TREATED: helpers & constants ---
  const makeIdxKey = (rIdx, cIdx) => `r${rIdx}-c${cIdx}`;

  const AREAS_TREATED_LAYOUT = [
    ["Office", "Tables", "Computer", "Cabinets", "Drawer"],
    ["Counter Area", "Cash register", "Telephone", "Counter table", null],
    [null, "Signage", "Softdrinks Dispensing machine", null, null],
    [null, "Soda fountain", "Checkout Stands", null, null],
    ["Dining Area", "Tables", "Chairs", "Walls", "Corners"],
    ["Kitchen", "Flooring", "Walling", "Corner", "Ceiling"],
    [null, "Sink", "Cabinets", "Tables", "Freezer"],
    [null, "Oven", "Fridge", "Chiller", "Kitch eq"],
    [null, "Meat counter", "Meat cutting blocks", null, "Pallets"],
    [null, "CR", "Drainage", null, null],
    ["Bedrooms", "Flooring", "Walling", "Bench", null],
    ["Dishwashing Area", "Sink", "Cabinet", "Table", null],
    ["Storage Area", "Freezer", "Coolers", null, null],
    ["Stock Area", "Cabinets", "Corners", null, null],
    ["Locker Room", "Lockers", "Wall", null, null],
    ["Crawl Space", "Stock items", "Fuse boxes", null, null],
    ["CR", "Sides/ Walls", "Switches", null, null],
    ["Receiving Area", "Sofa", "Canals", null, null],
    ["Hallway/ Stairway", "Corners", "Generator Rm", null, null],
    ["Electrical Circuits", "Switch boxes", "Guardhouse", null, null],
    [null, "Receptacle", "Refrigerators", null, null],
    [null, "Drainage", "Sinks", null, null],
    ["Surroundings", "Gas Room", "Electrical Rm", null, null],
    ["Other areas", "Drainage area", "Plants", null, null],
  ];

  // find the coordinates of a label (case-insensitive)
  const findLabelKey = (layout, search) => {
    const target = String(search).toLowerCase();
    for (let r = 0; r < layout.length; r++) {
      for (let c = 0; c < layout[r].length; c++) {
        const lbl = layout[r][c];
        if (lbl && String(lbl).toLowerCase() === target) {
          return makeIdxKey(r, c);
        }
      }
    }
    return null;
  };

  // Areas treated state (editable)
  const [areasTreatedState, setAreasTreatedState] = useState({});
  const [otherAreasText, setOtherAreasText] = useState("");

  const toggleAreaTreated = (key) => () => {
    setAreasTreatedState(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // toggle 'Other areas' text enablement only if "Other areas" cell is checked
  const OTHER_AREAS_KEY = React.useMemo(
    () => findLabelKey(AREAS_TREATED_LAYOUT, "Other areas"),
    []
  );

  // --- FINDINGS (editable) ---
  const DEGREE_ROWS = [
    { key: 'flies',      label: 'Flies' },
    { key: 'mosquitoes', label: 'Mosquitoes' },
    { key: 'roaches',    label: 'Roaches' },
    { key: 'ants',       label: 'Ants' },
    { key: 'rodents',    label: 'Rodents' },
  ];
  const DEGREE_LEVELS = ['L', 'M', 'H'];

  const ratingBtnSx = (selected) => ({
    fontSize: '1.25rem',
    minWidth: '40px',
    height: '40px',
    p: 0,
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? 'common.white' : 'text.secondary',
    border: selected ? 'none' : '1px solid',
    borderColor: selected ? 'transparent' : 'divider',
    transform: selected ? 'scale(1.2)' : 'scale(1)',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'grey.100',
    },
    cursor: 'pointer',
  });

  // Helper functions
  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters((prev) => {
      return { ...prev, [field]: value };
      // const newState = {
      //   ...prev,
      //   [field]: value,
      // };

      // // If changing the start date, ensure end date is not before start date
      // if (field === 'job_date' && prev.end_date && value && value > prev.end_date) {
      //   newState.end_date = value; // Set end date to start date if it's earlier
      // }

      // // If changing the end date, ensure it's not before start date
      // if (field === 'end_date' && prev.job_date && value && value < prev.job_date) {
      //   return prev; // Don't update if end date is before start date
      // }

      // return newState;
    });
  };

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map(t => t.id);
    setEditSelectedTechIds(ids);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const toggleEditTech = (techId) => (e) => {
    const checked = e.target.checked;
    setEditSelectedTechIds(prev => checked ? [...prev, techId] : prev.filter(id => id !== techId));
  };

  const saveEditTechs = async () => {
    // Early return if jobOrder is not loaded
    if (!jobOrder || !jobOrder.id) {
      setErrorMessage("Job order data is not available. Please refresh the page.");
      return;
    }

    const selected = (allTechnicians || [])
      .filter(t => editSelectedTechIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });
      // update local UI immediately
      setJobOrder(prev => ({ ...prev, technicians: selected }));

      // keep the names array in sync for your PDF payload
      setjobOrderParameters(prev => ({
        ...prev,
        accomplished_by_technician: selected.map(t => `${t.firstname} ${t.lastname}`)
      }));

      // ensure signatory stays valid
      const validNames = new Set(selected.map(t => `${t.firstname} ${t.lastname}`));
      setTechnicianSignatory(prev => (prev && validNames.has(prev)) ? prev : "");

      setSuccessMessage("Technicians updated.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setEditTechsOpen(false);
    }
  };

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        
        // Fetch job order data
        const jobOrderData = await getJobOrder(id);
        setJobOrder(jobOrderData || {});
        setSatisfactionRating(jobOrderData?.satisfaction_rating || 0);
        
        // Fetch technicians data
        const techniciansData = await getTechnicians();
        setAllTechnicians(techniciansData || []);
        
        // Fetch client only if needed
        if (jobOrderData?.client_id) {
          const clientData = await getClient(jobOrderData.client_id);
          setClient(clientData);
        }

        const technician_signed = jobOrderData?.technicians?.map(({ firstname, lastname }) => `${firstname} ${lastname}`) || [];

        setjobOrderParameters((prev) => ({
          ...prev,
          status: jobOrderData?.status || "",
          job_date: jobOrderData?.job_date || "",
          // end_date: jobOrderData?.end_date || "",
          start_time: jobOrderData?.start_time || "",
          end_time: jobOrderData?.end_time || "",
          accomplished_by_technician: technician_signed
        }));

        setEditedClient(jobOrderData?.client);

        // Initialize findings areas and recommendations
        if (jobOrderData?.findings_areas && Array.isArray(jobOrderData.findings_areas)) {
          replaceFindingAreas(jobOrderData.findings_areas);
        }

        if (jobOrderData?.recommendations && Array.isArray(jobOrderData.recommendations)) {
          setValue("recommendations", jobOrderData.recommendations);
        }

        // Initialize all form data immediately after fetching
        if (jobOrderData) {
          initializeFormData(jobOrderData);
        }

      } catch (error) {
        console.error("Error fetching job order:", error);
        setErrorMessage("Error fetching job order. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id, setValue, replaceFindingAreas]);

  // Separate function to initialize form data (called once after fetch)
  const initializeFormData = (jobOrderData) => {
    // Initialize Nature of Work
    const nature = jobOrderData?.nature_of_work ?? {};
    setNatureOfWork({
      generalPestControl: Boolean(nature.general_pest_control),
      disinfection: Boolean(nature.disinfection),
      fumigation: Boolean(nature.fumigation),
      rodentControl: Boolean(nature.rodent_control),
      foggingMisting: Boolean(nature.fogging_misting),
      others: Boolean(nature.others),
    });
    setNatureOfWorkOthersText(nature.others_text ?? "");

    // Initialize Structure Type
    const st = jobOrderData?.qc_structure_type ?? {};
    setStructureType({
      residential: Boolean(st.residential),
      commercialEstablishment: Boolean(st.commercial_establishment),
      others: Boolean(st.others),
    });
    setStructureNatureOfBusiness(st.nature_of_business ?? "");
    setStructureOthersText(st.others_text ?? "");

    // Initialize GPC data
    const gpcData = jobOrderData?.gpc ?? {};
    const insectsDetail = gpcData.insects_detail ?? {};
    setGpcFlyingCrawling(Boolean(gpcData.flying_and_crawling_insects));
    setGpcInsectsDetail({
      cockroach: Boolean(insectsDetail.cockroach),
      flies: Boolean(insectsDetail.flies),
      mosquitoes: Boolean(insectsDetail.mosquitoes),
    });
    setGpcRodents(Boolean(gpcData.rodents));
    setGpcOtherPests(Boolean(gpcData.other_pests));
    setGpcOtherPestsText(gpcData.other_pests_text ?? "");

    // Initialize Action Taken
    const act = jobOrderData?.action_taken_treatment_done ?? {};
    setActionTaken({
      mistSpraying: Boolean(act.mist_spraying),
      thermalFogging: Boolean(act.thermal_fogging),
      coldFogging: Boolean(act.cold_fogging),
      larviciding: Boolean(act.larviciding),
      gelBaiting: Boolean(act.gel_baiting),
      uvLight: Boolean(act.uv_light),
      wiping: Boolean(act.wiping),
      steaming: Boolean(act.steaming),
      others: Boolean(act.others),
    });
    setActionTakenOthersText(act.others_text ?? "");

    // Initialize Areas Treated
    const areasPos = jobOrderData?.areas_treated_positions ?? [];
    const areasState = {};
    for (const { row, col } of areasPos) {
      const rIdx = (Number(row) || 1) - 1;
      const cIdx = (Number(col) || 1) - 1;
      if (
        rIdx >= 0 &&
        cIdx >= 0 &&
        AREAS_TREATED_LAYOUT[rIdx] &&
        AREAS_TREATED_LAYOUT[rIdx][cIdx]
      ) {
        areasState[makeIdxKey(rIdx, cIdx)] = true;
      }
    }
    setAreasTreatedState(areasState);
    setOtherAreasText(jobOrderData?.areas_treated_other_text ?? "");

    // Initialize Customer Responsibilities
    const cr = jobOrderData?.customers_responsibilities ?? {};
    const sanitationList = Array.isArray(cr.sanitation) ? cr.sanitation : [];
    setCustomerResp({
      sanitation1: sanitationList[0] ?? "",
      sanitation2: sanitationList[1] ?? "",
      sanitation3: sanitationList[2] ?? "",
      sealing: cr.rodent_entry_point?.sealing ?? "",
      barriers: cr.rodent_entry_point?.barriers ?? "",
      repairsMaintenance: cr.repairs_maintenance ?? "",
      renovation: cr.renovation ?? "",
    });

    // Initialize Degree of Infestation
    const degree = jobOrderData?.findings_degree_of_infestation ?? {};
    setInfestationFindings({
      Roaches: degree.roaches ?? "",
      Mosquitoes: degree.mosquitoes ?? "",
      Flies: degree.flies ?? "",
      Ants: degree.ants ?? "",
      Rodents: degree.rodents ?? "",
    });

    // Initialize Chemical Used
    const chemicalsUsed = jobOrderData?.chemical_used ?? [];
    const newChemicals = [...chemical_used];
    chemicalsUsed.forEach((chem, index) => {
      if (index < newChemicals.length) {
        newChemicals[index] = {
          label: chem.label ?? (index + 1).toString(),
          chemical: chem.chemical ?? "",
        };
      }
    });
    setChemicalUsed(newChemicals);

    // Initialize Control Against
    const ca = jobOrderData?.control_against ?? {};
    setControlAgainst({
      crawlingInsects: Boolean(ca.crawling_insects),
      flyingInsects: Boolean(ca.flying_insects),
      rodentControl: Boolean(ca.rodent_control),
      others: Boolean(ca.others),
    });
    setOtherControlText(ca.others_text ?? "");

    // Initialize other fields
    setTechnicianSignatory(jobOrderData?.technician_signatory ?? "");
    setAcknowledgedClientSignatory(jobOrderData?.acknowledgedClientSignatory ?? "");
    setAccomplishedSignatureURL(jobOrderData?.accomplishedSignature ?? "");
    setAcknowledgedSignatureURL(jobOrderData?.acknowledgedSignature ?? "");
    setSpecialNotes(jobOrderData?.special_notes ?? "");
    setAppointmentType(jobOrderData?.appointment_type ?? "");
    setTreatmentType(jobOrderData?.treatment_type ?? "");

    // Initialize document links
    setLinks({
      certificate_url: jobOrderData?.certificate_url ?? "",
      receipts_url: jobOrderData?.receipts_url ?? "",
      reports_url: jobOrderData?.reports_url ?? "",
    });
  };

  // Convert booleans to "True"/"False" recursively (for PDF mapper payload)
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) return obj.map(convertBooleans);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, convertBooleans(v)]));
    }
    if (typeof obj === "boolean") return obj ? "True" : "False";
    return obj;
  };

  // Upload a generated PDF to S3 and return the public URL (QcForm pattern)
  const uploadPDF = async (pdfBlob, jobOrderId, type) => {
    const fileName = `${jobOrderId}-${type}.pdf`;
    const s3Uploader = type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
    const params = {
      Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
      Key: fileName,
      Body: pdfBlob,
      ContentType: "application/pdf",
      ContentDisposition: "inline",
      CacheControl: "public, max-age=31536000",
    };
    await s3Uploader.upload(params).promise();
    return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
  };

  const handleGeneratePDF = async (type, jobOrderId, pdfPayload) => {
    const pdfBlob = type === "invoice"
      ? await generateReceipts(pdfPayload)
      : await generateReports(pdfPayload);

    const s3Url = await uploadPDF(pdfBlob, jobOrderId, type);

    // Patch DynamoDB links to keep your “Document Links” section in sync
    await patchJobOrderLink(jobOrderId, {
      [type === "invoice" ? "receipts_url" : "reports_url"]: s3Url,
    });

    return s3Url;
  };

  // Date for email body (QcForm style)
  function formatDate(dateString) {
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) throw new Error("Invalid date");
      return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" }).format(d);
    } catch {
      return "Invalid Date";
    }
  }

  // Optional: ensures S3 serves PDFs inline (matches your no-auto-download preference)
  const asInline = (url) =>
    url ? `${url}${url.includes("?") ? "&" : "?"}response-content-disposition=inline` : url;

  const handleSendEmail = async () => {
    const generateAttachmentLinks = () => {
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        certificate_url:`${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url:   `${baseUrl}/${jobOrder.id}_final_receipt.pdf`,
      };
    };

    const attachmentLinks = generateAttachmentLinks();
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key.replace(/_url$/, "").replace(/_/g, " ").toUpperCase();
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html><head><meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
      body{font-family:Arial;margin:0;padding:0}
      .email-container{max-width:600px;margin:0 auto;padding:20px;border:1px solid #ddd}
      .header,.footer{text-align:center;padding:20px 0}
      .content{margin:20px 0}
      .content img{max-width:100%;height:auto;display:block;margin:0 auto}
    </style></head><body>
      <div class="email-container">
        <div class="header">
          <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header">
        </div>
        <div class="content">
          <p>Sir/Ma'am,</p>
          <p>Good day!</p>
          <p>We are pleased to send you our Accomplishment Report for the visit done on ${formatDate(jobOrder?.job_date)}.</p>
          <p>We thank you and please let us know if you have any questions.</p>
          <p>Sincerely,<br/>Boaz Pest Control - Accounts Team</p>
          <ul>${attachmentLinksHTML}</ul>
          <hr/>
        </div>
        <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration:none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer">
            <hr/>
            <footer><p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p></footer>
          </div>
        </a>
      </div>
    </body></html>`;

    try {
      await sendEmailQueue({
        from: "boaz-reminders@maestrosystemsinc.com",
        recipients: [jobOrder?.client?.contact?.email?.email],
        subject: `Service Report for Job Order ${jobOrder?.id}`,
        body: htmlBody,
      });
      setSuccessMessage("Email sent successfully!");
    } catch (err) {
      console.error("Error sending email:", err);
      setErrorMessage("Failed to send email.");
    }
  };

  // Form submission - optimized
  const onSubmit = async (formData) => {
    // Early return if jobOrder is not loaded
    if (!jobOrder || !jobOrder.id) {
      setErrorMessage("Job order data is not available. Please refresh the page.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Prepare areas treated positions
      const selectedPositions = [];
      Object.entries(areasTreatedState).forEach(([key, selected]) => {
        if (selected) {
          const [r, c] = key.replace('r', '').split('-c').map(Number);
          selectedPositions.push({ row: r + 1, col: c + 1 }); // 1-based for DB
        }
      });

      const updatedJobOrderParameters = {
        ...jobOrder,
        status: jobOrderParameters.status,
        job_date: jobOrderParameters.job_date,
        // end_date: jobOrderParameters.end_date,
        start_time: jobOrderParameters.start_time,
        end_time: jobOrderParameters.end_time,
        appointment_type: appointmentType,
        treatment_type: treatmentType,
        special_notes: specialNotes,
        chemical_used: chemical_used.filter(c => c.chemical.trim()),
        control_against: {
          crawling_insects: controlAgainst.crawlingInsects,
          flying_insects: controlAgainst.flyingInsects,
          rodent_control: controlAgainst.rodentControl,
          others: controlAgainst.others,
          others_text: otherControlText.trim(),
        },
        nature_of_work: {
          general_pest_control: natureOfWork.generalPestControl,
          disinfection: natureOfWork.disinfection,
          fumigation: natureOfWork.fumigation,
          rodent_control: natureOfWork.rodentControl,
          fogging_misting: natureOfWork.foggingMisting,
          others: natureOfWork.others,
          others_text: natureOfWorkOthersText.trim(),
        },
        qc_structure_type: {
          residential: structureType.residential,
          commercial_establishment: structureType.commercialEstablishment,
          nature_of_business: structureNatureOfBusiness.trim(),
          others: structureType.others,
          others_text: structureOthersText.trim(),
        },
        gpc: {
          flying_and_crawling_insects: gpcFlyingCrawling,
          insects_detail: gpcInsectsDetail,
          rodents: gpcRodents,
          other_pests: gpcOtherPests,
          other_pests_text: gpcOtherPestsText.trim(),
        },
        action_taken_treatment_done: {
          mist_spraying: actionTaken.mistSpraying,
          thermal_fogging: actionTaken.thermalFogging,
          cold_fogging: actionTaken.coldFogging,
          larviciding: actionTaken.larviciding,
          gel_baiting: actionTaken.gelBaiting,
          uv_light: actionTaken.uvLight,
          wiping: actionTaken.wiping,
          steaming: actionTaken.steaming,
          others: actionTaken.others,
          others_text: actionTakenOthersText.trim(),
        },
        areas_treated_positions: selectedPositions,
        areas_treated_other_text: otherAreasText.trim(),
        customers_responsibilities: {
          sanitation: [customerResp.sanitation1, customerResp.sanitation2, customerResp.sanitation3],
          rodent_entry_point: {
            sealing: customerResp.sealing,
            barriers: customerResp.barriers          
          },
          repairs_maintenance: customerResp.repairsMaintenance,
          renovation: customerResp.renovation,
        },
        findings_degree_of_infestation: {
          flies: infestationFindings.Flies,
          mosquitoes: infestationFindings.Mosquitoes,
          roaches: infestationFindings.Roaches,
          ants: infestationFindings.Ants,
          rodents: infestationFindings.Rodents,
        },
        findings_areas: formData.findingsAreas || [],
        recommendations: formData.recommendations || [],
        service_report_status: "Done",
        technician_signatory: technicianSignatory,
        satisfaction_rating: satisfactionRating,
        accomplishedSignature: accomplishedSignatureURL,
        acknowledgedSignature: acknowledgedSignatureURL,
        acknowledgedClientSignatory: acknowledgedClientSignatory,
      };

      // 2) Persist edits
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      if (editedClient?.id) {
        const { id: clientId, ...putClientParameters } = editedClient;
        await putClient(clientId, putClientParameters);
      }

      // 3) Regenerate PDFs via mapper (certificate & final receipt)
      const mapperPayload = convertBooleans({
        jobOrder,
        updatedJobOrderParameters,
        form: formData,
      });
      await submitDocumentFieldMapper(mapperPayload);

      // 4) Patch or compute S3 links and update local UI state
      const newLinks = {
        certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
        receipts_url:    `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`,
      };

      await patchJobOrderLink(jobOrder.id, newLinks);
      setLinks(newLinks);

      // 5) Send email using the new helper (same pattern as EditMakatiGpcForm)
      await handleSendEmail();

    setSuccessMessage("QC form updated, PDFs regenerated, and email queued!");

    } catch (error) {
      console.error("Error updating QC form:", error);
      setErrorMessage(`Failed to update QC Form: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  
  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleFormSubmit(onSubmit)} className="job-order-form">
        <Grid container>
        {/* Basic Information Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Basic Information
                </Typography>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {" "}
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={12} xl={2}>
                  <TextField
                    name="id"
                    label="Job Order ID"
                    value={jobOrder?.id || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={12} xl={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="job-order-status-label">
                      Job Order Status
                    </InputLabel>
                    <Select
                      labelId="job-order-status-label"
                      name="status"
                      value={jobOrderParameters?.status || ""}
                      label="Job Order Status"
                      onChange={(e) =>
                        handleJObOrderChange("status", e.target.value)
                      }
                    >
                      <MenuItem value="Not Done">Not Done</MenuItem>
                      <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                      <MenuItem value="Done">Done</MenuItem>
                      <MenuItem value="Cancelled By Client">
                        Cancelled By Client
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client ID"
                    label="Client ID"
                    value={jobOrder?.client?.id || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client Name"
                    label="Client Name"
                    value={jobOrder?.client?.name || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={10}>
                      <TextField
                        label="Job Site Address"
                        variant="outlined"
                        fullWidth
                        value={
                          jobOrder?.client?.address
                            ? `${jobOrder?.client?.address?.house_unit_street || ""
                            }, ${jobOrder?.client?.address?.barangay || ""
                            }, ${jobOrder?.client?.address?.city || ""
                            }, ${jobOrder?.client?.address?.metro_province || ""} ${jobOrder?.client?.address?.zipcode || ""}`
                            : ""
                        }
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                      <TextField
                        name="area"
                        label="Area"
                        value={jobOrder?.client?.address?.area || ""}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    variant="outlined"
                    value={
                      jobOrder?.client?.contact
                        ? `${jobOrder?.client?.contact?.lastname || ""}, ${jobOrder?.client?.contact?.firstname || ""
                        }`
                        : ""
                    } // Display the contact's full name or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    variant="outlined"
                    value={jobOrder?.client?.contact?.email?.email || ""} // Display the contact email or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    variant="outlined"
                    value={
                      jobOrder?.client?.contact?.number
                        ? `${jobOrder?.client?.contact?.number?.type}: ${jobOrder?.client?.contact?.number?.number}`
                        : ""
                    } // Display the contact number or an empty string if not available
                    disabled
                  />
                </Grid>                
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    label="Location Type"
                    value={
                      client?.location_type
                        ? client.location_type
                        : ""
                    }
                    fullWidth
                    variant="outlined"
                    sx={{ fontSize: "0.875rem" }}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Type Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobTypes.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobDescriptions.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Special Notes Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{                    
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Special Notes
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="special_notes"
                    type="text"
                    value={specialNotes}
                    onChange={(e) => setSpecialNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appointment Section */}
        <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
            >
            <Card
                variant="outlined"
                sx={{
                    marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                    flex: 1,
                }}
            >
                <CardHeader
                title={
                    <Typography
                    variant="h6"
                    sx={{
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                    }}
                    >
                    Appointment
                    </Typography>
                }
                />
                <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12} lg={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="appointment-type-label">
                        Appointment Type
                        </InputLabel>
                        <Select
                        labelId="appointment-type-label"
                        name="appointment_type"
                        value={appointmentType}
                        label="Appointment Type"
                        onChange={(e) => setAppointmentType(e.target.value)}
                        disabled
                        >
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                        </Select>
                    </FormControl>
                    </Grid>
                    <Grid item xs={12} md={12} lg={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="treatment-type-label">
                        Treatment Type
                        </InputLabel>
                        <Select
                        labelId="treatment-type-label"
                        name="treatment_type"
                        value={treatmentType}
                        label="Treatment Type"
                        onChange={(e) => setTreatmentType(e.target.value)}
                        disabled
                        >
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                        </Select>
                        </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Grid>

        {/* Nature of Work Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  NATURE OF WORK
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={natureOfWork.generalPestControl}
                      onChange={handleNatureOfWorkChange("generalPestControl")}
                    />
                  }
                  label="General Pest Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={natureOfWork.disinfection}
                      onChange={handleNatureOfWorkChange("disinfection")}
                    />
                  }
                  label="Disinfection"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={natureOfWork.fumigation}
                      onChange={handleNatureOfWorkChange("fumigation")}
                    />
                  }
                  label="Fumigation"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={natureOfWork.rodentControl}
                      onChange={handleNatureOfWorkChange("rodentControl")}
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={natureOfWork.foggingMisting}
                      onChange={handleNatureOfWorkChange("foggingMisting")}
                    />
                  }
                  label="Fogging/Misting"
                />

                {/* Others with text field beside it */}
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={natureOfWork.others}
                        onChange={handleNatureOfWorkChange("others")}
                      />
                    }
                    label="Others"
                  />
                  <TextField
                    label="Please specify"
                    value={natureOfWorkOthersText}
                    onChange={(e) => setNatureOfWorkOthersText(e.target.value)}
                    disabled={!natureOfWork.others}
                    size="small"
                    sx={{ maxWidth: 320 }}
                  />
                </Box>
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Structure Type Section */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                TYPE OF STRUCTURE
              </Typography>
            }
            sx={{ pb: 0, px: 2, pt: 2 }}
          />
          <CardContent sx={{ px: 2, pb: 2 }}>
            <FormGroup sx={{ width: "100%" }}>
              {/* Residential */}
              <FormControlLabel
                sx={{ width: "100%" }}
                control={
                  <Checkbox 
                    checked={structureType.residential} 
                    onChange={handleStructureTypeChange("residential")}
                  />
                }
                label="Residential"
              />

              {/* Commercial Establishment + Nature of Business text field */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={structureType.commercialEstablishment} 
                      onChange={handleStructureTypeChange("commercialEstablishment")}
                    />
                  }
                  label="Commercial Establishment"
                />
                <TextField
                  label="Nature of Business"
                  size="small"
                  sx={{ maxWidth: 320 }}
                  value={structureNatureOfBusiness}
                  onChange={(e) => setStructureNatureOfBusiness(e.target.value)}
                  disabled={!structureType.commercialEstablishment}
                />
              </Box>

              {/* Others + specify text field */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={structureType.others} 
                      onChange={handleStructureTypeChange("others")}
                    />
                  }
                  label="Others"
                />
                <TextField
                  label="Please specify"
                  size="small"
                  sx={{ maxWidth: 320 }}
                  value={structureOthersText}
                  onChange={(e) => setStructureOthersText(e.target.value)}
                  disabled={!structureType.others}
                />
              </Box>
            </FormGroup>
          </CardContent>
        </Card>

        {/* For General Pest Control (GPC) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For General Pest Control (GPC)
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup>

                {/* Main: Flying & crawling insects */}
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={gpcFlyingCrawling} 
                      onChange={(e) => setGpcFlyingCrawling(e.target.checked)}
                    />
                  }
                  label="Flying and crawling insects"
                />

                {/* Sub-options (editable) */}
                <Box
                  sx={{
                    ml: 4,
                    mt: 0.5,
                    pl: 1,
                    borderLeft: gpcFlyingCrawling ? '2px solid' : '2px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox 
                          size="small" 
                          checked={gpcInsectsDetail.cockroach} 
                          onChange={(e) => setGpcInsectsDetail(prev => ({ ...prev, cockroach: e.target.checked }))}
                          disabled={!gpcFlyingCrawling}
                        />
                      }
                      label="Cockroach"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          size="small" 
                          checked={gpcInsectsDetail.flies} 
                          onChange={(e) => setGpcInsectsDetail(prev => ({ ...prev, flies: e.target.checked }))}
                          disabled={!gpcFlyingCrawling}
                        />
                      }
                      label="Flies"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          size="small" 
                          checked={gpcInsectsDetail.mosquitoes} 
                          onChange={(e) => setGpcInsectsDetail(prev => ({ ...prev, mosquitoes: e.target.checked }))}
                          disabled={!gpcFlyingCrawling}
                        />
                      }
                      label="Mosquitoes"
                    />
                  </Stack>
                </Box>

                {/* Main: Rodents */}
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={gpcRodents} 
                      onChange={(e) => setGpcRodents(e.target.checked)}
                    />
                  }
                  label="Rodents"
                />

                {/* Main: Other pests + text field beside it */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={gpcOtherPests} 
                        onChange={(e) => setGpcOtherPests(e.target.checked)}
                      />
                    }
                    label="Other pests"
                  />
                  <TextField
                    label="Please specify"
                    size="small"
                    disabled={!gpcOtherPests}
                    value={gpcOtherPestsText}
                    onChange={(e) => setGpcOtherPestsText(e.target.value)}
                    sx={{ flex: '1 1 260px', maxWidth: 360 }}
                  />
                </Box>

              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* ACTION TAKEN / TREATMENT DONE */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  ACTION TAKEN / TREATMENT DONE
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup>

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.mistSpraying} 
                      onChange={handleActionTakenChange("mistSpraying")}
                    />
                  }
                  label="Mist spraying"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.thermalFogging} 
                      onChange={handleActionTakenChange("thermalFogging")}
                    />
                  }
                  label="Thermal fogging"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.coldFogging} 
                      onChange={handleActionTakenChange("coldFogging")}
                    />
                  }
                  label="Cold fogging"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.larviciding} 
                      onChange={handleActionTakenChange("larviciding")}
                    />
                  }
                  label="Larviciding"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.gelBaiting} 
                      onChange={handleActionTakenChange("gelBaiting")}
                    />
                  }
                  label="Gel baiting"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.uvLight} 
                      onChange={handleActionTakenChange("uvLight")}
                    />
                  }
                  label="UV light"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.wiping} 
                      onChange={handleActionTakenChange("wiping")}
                    />
                  }
                  label="Wiping"
                />

                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={actionTaken.steaming} 
                      onChange={handleActionTakenChange("steaming")}
                    />
                  }
                  label="Steaming"
                />

                {/* Others + text field beside it */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={actionTaken.others} 
                        onChange={handleActionTakenChange("others")}
                      />
                    }
                    label="Others:"
                  />
                  <TextField
                    label="Please specify"
                    size="small"
                    value={actionTakenOthersText}
                    onChange={(e) => setActionTakenOthersText(e.target.value)}
                    disabled={!actionTaken.others}
                    sx={{ flex: '1 1 260px', maxWidth: 360 }}
                  />
                </Box>

              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* AREAS TREATED Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  AREAS TREATED
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <AreasTable
                  layout={AREAS_TREATED_LAYOUT}
                  state={areasTreatedState}
                  onToggle={toggleAreaTreated}
                  ariaLabel="areas_treated"
                  getKey={(rIdx, cIdx /*, label */) => makeIdxKey(rIdx, cIdx)}
                />

                <TextField
                  label="Please specify (Other areas)"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  value={otherAreasText}
                  onChange={(e) => setOtherAreasText(e.target.value)}
                  disabled={!OTHER_AREAS_KEY || !areasTreatedState[OTHER_AREAS_KEY]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* FINDINGS */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  FINDINGS
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {/* Sub-header */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Degree of Infestation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                L - Light, M - Medium, H - High
              </Typography>

              {/* Pest rows (editable) */}
              <Grid container spacing={1.5}>
                {DEGREE_ROWS.map(({ key, label }) => {
                  const selectedLV = (infestationFindings?.[label] || '').toUpperCase(); // 'L'|'M'|'H'|''

                  return (
                    <Grid item xs={12} key={key}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.25,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {label}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 4 }}>
                          {DEGREE_LEVELS.map((lv) => {
                            const isSelected = selectedLV === lv;
                            return (
                              <Button
                                key={lv}
                                onClick={() => handleInfestationChange(label, isSelected ? '' : lv)}
                                sx={ratingBtnSx(isSelected)}
                                aria-label={`${label} ${lv}`}
                              >
                                {lv}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* FINDINGS – Areas list (editable) */}
              <Box sx={{ mt: 2 }}>
                {findingsAreas.map((f, idx) => (
                  <FindingsCard
                    key={f.id}
                    control={control}
                    register={register}
                    index={idx}
                    removeFinding={removeFinding}
                  />
                ))}

                {findingsAreas.length < 6 && (
                  <Button
                    variant="outlined"
                    onClick={() => addFinding({
                      location: "",
                      pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 }
                    })}
                    sx={{ mt: 1 }}
                  >
                    Add Finding Area ({findingsAreas.length}/6)
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RECOMMENDATIONS */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  RECOMMENDATIONS
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Add up to {MAX_RECOMMENDATIONS} recommendations
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                {recFields.map((f, idx) => {
                  const selected = recValues?.[idx]?.option ?? "";

                  return (
                    <Grid item xs={12} key={f.id}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {/* Dropdown */}
                        <Controller
                          control={control}
                          name={`recommendations.${idx}.option`}
                          defaultValue={f.option ?? ""}
                          render={({ field }) => (
                            <Select
                              {...field}
                              size="small"
                              displayEmpty
                              sx={{ minWidth: 260 }}
                              onChange={(e) => {
                                field.onChange(e);
                                if (e.target.value !== "Other") {
                                  // clear free-text if leaving "Other"
                                  setValue(`recommendations.${idx}.other_text`, "");
                                }
                              }}
                            >
                              {RECOMMENDATION_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value || "none"} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />

                        {/* Free-text when "Other" is selected */}
                        {selected === "Other" && (
                          <TextField
                            size="small"
                            label="Specify recommendation"
                            sx={{ flex: '1 1 260px', minWidth: 240 }}
                            {...register(`recommendations.${idx}.other_text`)}
                          />
                        )}

                        {/* Remove row */}
                        <Box sx={{ ml: 'auto' }}>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => removeRec(idx)}
                            aria-label="Remove recommendation"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <Button
                size="small"
                variant="outlined"
                disabled={recFields.length >= MAX_RECOMMENDATIONS}
                onClick={() => {
                  if (recFields.length >= MAX_RECOMMENDATIONS) return;
                  appendRec({ option: "", other_text: "" });
                }}
                sx={{ mt: 2 }}
              >
                Add Recommendation ({recFields.length}/{MAX_RECOMMENDATIONS})
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* CUSTOMER'S RESPONSIBILITIES Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  CUSTOMER'S RESPONSIBILITIES
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />

            <CardContent sx={{ px: 2, pb: 2 }}>
              {/* SANITATION */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  SANITATION
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Needs improvement (Specify area)
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Specify area 1"
                      value={customerResp.sanitation1}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, sanitation1: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Specify area 2"
                      value={customerResp.sanitation2}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, sanitation2: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Specify area 3"
                      value={customerResp.sanitation3}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, sanitation3: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* RODENT ENTRY POINT */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  RODENT ENTRY POINT
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Needs improvement (Specify area)
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small"
                      label="Sealing"
                      value={customerResp.sealing}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, sealing: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth size="small"
                      label="Barriers"
                      value={customerResp.barriers}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, barriers: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Repairs & Maintenance / Renovation */}
              <Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Repairs & Maintenance (Specify area)"
                      value={customerResp.repairsMaintenance}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, repairsMaintenance: e.target.value }))}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small"
                      label="Renovation"
                      value={customerResp.renovation}
                      onChange={(e) => setCustomerResp(prev => ({ ...prev, renovation: e.target.value }))}
                    />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chemical Used Section */}
        <Grid item xs={12} md={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center" colSpan={2} style={{ fontWeight: "bold" }}>
                          CHEMICAL/S USED
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chemical_used.map((chemical, index) => (
                        <TableRow key={`${chemical.label}-${index}`}>
                          <TableCell>{chemical.label}:</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chemical.chemical}
                              onChange={(e) => handleChemicalInputChange(index, "chemical", e.target.value)}
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Control Against Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                >
                  CONTROL AGAINST
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              For Certificate
            </Typography>
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={controlAgainst.crawlingInsects}
                      onChange={handleControlAgainstChange("crawlingInsects")}
                    />
                  }
                  label="Crawling Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={controlAgainst.flyingInsects}
                      onChange={handleControlAgainstChange("flyingInsects")}
                    />
                  }
                  label="Flying Insects"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={controlAgainst.rodentControl}
                      onChange={handleControlAgainstChange("rodentControl")}
                    />
                  }
                  label="Rodent Control"
                />

                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={controlAgainst.others}
                      onChange={handleControlAgainstChange("others")}
                    />
                  }
                  label="Others"
                />
              </FormGroup>

              {controlAgainst.others && (
                <TextField
                  label="Please specify"
                  value={otherControlText}
                  onChange={(e) => setOtherControlText(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Service Accomplished by Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Service Accomplished by:
              </Typography>
              <Grid container spacing={2}>
                {/* Remove Edit Technicians Button - now disabled/hidden */}
                
                { (jobOrder?.technicians || [])
                    .map((tech, i) => (
                  <Grid key={i} item xs={12}>
                    <TextField
                      label={`Technician ${i + 1}`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={`${tech.firstname} ${tech.lastname}`}
                      disabled
                    />
                  </Grid>
                ))}

                {/* Accomplished Signature - Display only */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Technician Signature:
                  </Typography>
                  
                  {/* Display existing signature if available */}
                  {accomplishedSignatureURL ? (
                    <Box
                      component="img"
                      src={accomplishedSignatureURL}
                      alt="Accomplished Signature"
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: 120,
                        objectFit: "contain",
                        border: "1px solid rgba(0,0,0,0.3)",
                        mb: 2,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: 120,
                        border: "1px solid rgba(0,0,0,0.3)",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No signature available
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {/* Technician Signatory - Display only */}
                <Grid item xs={12}>
                  <TextField
                    label="Technician Signatory"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={technicianSignatory}
                    disabled
                  />
                </Grid>

                {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder?.technician_signature_uploaded_at && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                      <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        🔵 Technician Signature Uploaded At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                        {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Schedule
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Date"
                    name="job_date"
                    type="date"
                    value={jobOrderParameters?.job_date || ""}
                    onChange={(e) =>
                      handleJObOrderChange("job_date", e.target.value)
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="start_time"
                    type="time"
                    value={jobOrderParameters?.start_time || ""}
                    onChange={(e) =>
                      handleJObOrderChange("start_time", e.target.value)
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={jobOrderParameters?.end_date || ""}
                    onChange={(e) =>
                      handleJObOrderChange("end_date", e.target.value)
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputProps: {
                        min: jobOrderParameters?.job_date || undefined
                      }
                    }}
                  />
                </Grid> */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="end_time"
                    type="time"
                    value={jobOrderParameters?.end_time || ""}
                    onChange={(e) =>
                      handleJObOrderChange("end_time", e.target.value)
                    }
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      
        {/* Acknowledged Signature Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For Client Only
                </Typography>
              }
              sx={{ px: 2, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Your Satisfaction Rating:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                (1 – lowest, 5 – highest)
              </Typography>
              <Rating
                name="satisfaction-rating"
                value={satisfactionRating}
                onChange={(event, newValue) => {
                  setSatisfactionRating(newValue || 0);
                }}
                readOnly
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Acknowledged By:
              </Typography>
              
              {/* Display existing signature if available */}
              {acknowledgedSignatureURL ? (
                <Box
                  component="img"
                  src={acknowledgedSignatureURL}
                  alt="Acknowledged Signature"
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 120,
                    objectFit: "contain",
                    border: "1px solid rgba(0,0,0,0.3)",
                    mb: 2,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 120,
                    border: "1px solid rgba(0,0,0,0.3)",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No signature available
                  </Typography>
                </Box>
              )}
              
              <TextField
                label="Client Representative"
                variant="outlined"
                fullWidth
                size="small"
                value={acknowledgedClientSignatory}
                disabled
              />

              {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
              {!isTechnician && jobOrder?.client_signature_uploaded_at && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    🟢 Client Signature Uploaded At:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                    {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                      month: 'short',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Document Links Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Document Links
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                {(jobOrder?.receipts_url || links?.receipts_url) ||
                  (jobOrder?.certificate_url || links?.certificate_url) ? (
                  <>
                    {(jobOrder?.receipts_url || links?.receipts_url) && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={`${links.receipts_url}?v=${encodeURIComponent(jobOrder?.timestamp_update || Date.now())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Receipts.pdf{" "}
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {(jobOrder?.certificate_url || links?.certificate_url) && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={`${links.certificate_url}?v=${encodeURIComponent(jobOrder?.timestamp_update || Date.now())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Certificate.pdf
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}

                    {/* {jobOrder?.reports_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={jobOrder.reports_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Reports.pdf
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )} */}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                      No document links available.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && jobOrder?.report_submitted_at && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                📋 Report Submitted At:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Save and Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ fontSize: "0.875rem" }}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Grid>

        {/* Edit Technicians Dialog */}
        <Dialog open={editTechsOpen} onClose={closeEditTechs} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Technicians</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              {allTechnicians.map((tech) => (
                <FormControlLabel
                  key={tech.id}
                  control={
                    <Checkbox
                      checked={editSelectedTechIds.includes(tech.id)}
                      onChange={toggleEditTech(tech.id)}
                    />
                  }
                  label={`${tech.firstname} ${tech.lastname}`}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditTechs} color="secondary">
              Cancel
            </Button>
            <Button onClick={saveEditTechs} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
      </form>
    </BaseFormWrapper>
  );
}

export default EditQcForm;
